import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    Switch,
    Button,
    Space,
    Divider,
    message,
    Spin,
} from "antd";

import {
    PlusOutlined,
    DollarOutlined,
    CloseOutlined,
} from "@ant-design/icons";

import api from "../api/api";


const { Option } = Select;


/* =========================================================
   MONTHS
========================================================= */

const MONTHS = [
    {
        value: "january",
        label: "January",
    },
    {
        value: "february",
        label: "February",
    },
    {
        value: "march",
        label: "March",
    },
    {
        value: "april",
        label: "April",
    },
    {
        value: "may",
        label: "May",
    },
    {
        value: "june",
        label: "June",
    },
    {
        value: "july",
        label: "July",
    },
    {
        value: "august",
        label: "August",
    },
    {
        value: "september",
        label: "September",
    },
    {
        value: "october",
        label: "October",
    },
    {
        value: "november",
        label: "November",
    },
    {
        value: "december",
        label: "December",
    },
];


/* =========================================================
   FEES FORM
========================================================= */

const FeesForm = ({
    open,
    mode = "payment",
    editingPayment = null,

    feeTypes = [],

    /*
     * Kept for compatibility with the parent component.
     *
     * We DO NOT depend on this list for the student
     * autocomplete anymore.
     */
    students = [],

    classes = [],
    selectedYear,

    onClose,
    onFeeTypeCreated,
    onPaymentCreated,
    onPaymentUpdated,
}) => {

    const [form] = Form.useForm();


    /* =====================================================
       GENERAL STATE
    ===================================================== */

    const [loading, setLoading] =
        useState(false);

    const [previousBalance, setPreviousBalance] =
        useState(null);

    const [
        requiresManualPayerName,
        setRequiresManualPayerName,
    ] = useState(false);


    /* =====================================================
       STUDENT AUTOCOMPLETE STATE
    ===================================================== */

    const [
        studentOptions,
        setStudentOptions,
    ] = useState([]);

    const [
        studentLoading,
        setStudentLoading,
    ] = useState(false);

    const [
        studentSearch,
        setStudentSearch,
    ] = useState("");

    const [
        studentPage,
        setStudentPage,
    ] = useState(1);

    const [
        studentHasMore,
        setStudentHasMore,
    ] = useState(true);


    /* =====================================================
       SEARCH DEBOUNCE
    ===================================================== */

    const studentSearchTimer =
        useRef(null);


    /* =====================================================
       FEE TYPE MAP
    ===================================================== */

    const feeTypeMap = useMemo(() => {

        return feeTypes.reduce(
            (map, fee) => {

                map[fee.id] = fee;

                return map;

            },
            {}
        );

    }, [feeTypes]);


    /* =====================================================
       NORMALIZE STUDENT
    ===================================================== */

    const normalizeStudent =
        useCallback(
            student => {

                if (!student) {
                    return null;
                }


                const fullName =
                    student.full_name ||
                    [
                        student.first_name,
                        student.middle_name,
                        student.last_name,
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .trim();


                return {
                    ...student,

                    full_name:
                        fullName ||
                        `Student #${student.id}`,
                };

            },
            []
        );


    /* =====================================================
       MERGE STUDENTS
       
       Prevent duplicate students when loading page 2,
       page 3, etc.
    ===================================================== */

    const mergeStudents =
        useCallback(
            (
                existing,
                incoming
            ) => {

                const map = new Map();


                existing.forEach(
                    student => {

                        if (
                            student &&
                            student.id !==
                                undefined &&
                            student.id !==
                                null
                        ) {

                            map.set(
                                String(
                                    student.id
                                ),
                                student
                            );

                        }

                    }
                );


                incoming.forEach(
                    student => {

                        if (
                            student &&
                            student.id !==
                                undefined &&
                            student.id !==
                                null
                        ) {

                            map.set(
                                String(
                                    student.id
                                ),
                                student
                            );

                        }

                    }
                );


                return Array.from(
                    map.values()
                );

            },
            []
        );


    /* =====================================================
       LOAD STUDENTS
       
       IMPORTANT:
       
       This uses the SAME API used elsewhere:
       
       /api/users/students/
       
       Search is performed by Django/DRF.
    ===================================================== */

    const loadStudents =
        useCallback(
            async ({
                search = "",
                page = 1,
                append = false,
            } = {}) => {

                try {

                    setStudentLoading(
                        true
                    );


                    const response =
                        await api.get(
                            "/api/users/students/",
                            {
                                params: {
                                    search:
                                        search.trim() ||
                                        undefined,

                                    page,
                                },
                            }
                        );


                    const data =
                        response.data;


                    /*
                     * DRF pagination normally returns:
                     *
                     * {
                     *     count: 1000,
                     *     next: "...",
                     *     previous: null,
                     *     results: [...]
                     * }
                     *
                     * We also support a plain array.
                     */

                    const rawStudents =
                        Array.isArray(data)
                            ? data
                            : Array.isArray(
                                data?.results
                            )
                                ? data.results
                                : [];


                    const normalized =
                        rawStudents
                            .map(
                                normalizeStudent
                            )
                            .filter(Boolean);


                    setStudentOptions(
                        previous => {

                            if (!append) {

                                return normalized;

                            }


                            return mergeStudents(
                                previous,
                                normalized
                            );

                        }
                    );


                    setStudentPage(
                        page
                    );


                    /*
                     * DRF pagination:
                     *
                     * next = null
                     *
                     * means there are no more
                     * students.
                     */

                    if (
                        data &&
                        Object.prototype.hasOwnProperty.call(
                            data,
                            "next"
                        )
                    ) {

                        setStudentHasMore(
                            Boolean(
                                data.next
                            )
                        );

                    } else {

                        /*
                         * Non-paginated response.
                         */

                        setStudentHasMore(
                            false
                        );

                    }

                } catch (error) {

                    console.error(
                        "Student loading error:",
                        error
                    );


                    /*
                     * Don't show an error every time
                     * the user types.
                     *
                     * The dropdown itself will show
                     * "No students found".
                     */

                    setStudentOptions(
                        []
                    );

                    setStudentHasMore(
                        false
                    );


                    message.error(
                        "Unable to search students."
                    );

                } finally {

                    setStudentLoading(
                        false
                    );

                }

            },
            [
                normalizeStudent,
                mergeStudents,
            ]
        );


    /* =====================================================
       LOAD FIRST PAGE WHEN MODAL OPENS
    ===================================================== */

    useEffect(() => {

        if (!open) {
            return;
        }


        if (mode === "fee_type") {
            return;
        }


        if (
            requiresManualPayerName
        ) {
            return;
        }


        setStudentOptions([]);

        setStudentSearch("");

        setStudentPage(1);

        setStudentHasMore(true);


        loadStudents({
            search: "",
            page: 1,
            append: false,
        });

    }, [
        open,
        mode,
        requiresManualPayerName,
        loadStudents,
    ]);


    /* =====================================================
       CLEANUP DEBOUNCE TIMER
    ===================================================== */

    useEffect(() => {

        return () => {

            if (
                studentSearchTimer.current
            ) {

                clearTimeout(
                    studentSearchTimer.current
                );

            }

        };

    }, []);


    /* =====================================================
       LOAD STUDENT FOR EDITING
       
       If editing payment belongs to a student that is
       not on page 1, fetch that student directly.
    ===================================================== */

    useEffect(() => {

        if (!open) {
            return;
        }


        if (
            mode === "fee_type"
        ) {
            return;
        }


        if (!editingPayment) {
            return;
        }


        const studentId =
            editingPayment.student;


        if (!studentId) {
            return;
        }


        const alreadyLoaded =
            studentOptions.some(
                student =>
                    String(
                        student.id
                    ) ===
                    String(
                        studentId
                    )
            );


        if (alreadyLoaded) {
            return;
        }


        let cancelled =
            false;


        const loadSelectedStudent =
            async () => {

                try {

                    const response =
                        await api.get(
                            `/api/users/students/${studentId}/`
                        );


                    if (cancelled) {
                        return;
                    }


                    const student =
                        normalizeStudent(
                            response.data
                        );


                    if (!student) {
                        return;
                    }


                    setStudentOptions(
                        previous =>
                            mergeStudents(
                                [
                                    student,
                                ],
                                previous
                            )
                    );

                } catch (error) {

                    console.error(
                        "Unable to load selected student:",
                        error
                    );

                }

            };


        loadSelectedStudent();


        return () => {

            cancelled = true;

        };

    }, [
        open,
        mode,
        editingPayment,
        studentOptions,
        normalizeStudent,
        mergeStudents,
    ]);


    /* =====================================================
       FORM INITIALIZATION
    ===================================================== */

    useEffect(() => {

        if (!open) {
            return;
        }


        if (
            mode === "fee_type"
        ) {

            form.resetFields();

            setRequiresManualPayerName(
                false
            );

            setPreviousBalance(
                null
            );

            return;

        }


        if (editingPayment) {

            form.setFieldsValue({

                fee_type:
                    editingPayment.fee_type,

                student:
                    editingPayment.student,

                requires_manual_payer_name:
                    editingPayment.requires_manual_payer_name,

                payer_name:
                    editingPayment.payer_name,

                total_amount:
                    Number(
                        editingPayment.total_amount
                    ),

                amount_paid:
                    Number(
                        editingPayment.amount_paid
                    ),

                month:
                    editingPayment.month,

            });


            setRequiresManualPayerName(
                Boolean(
                    editingPayment.requires_manual_payer_name
                )
            );

        } else {

            form.resetFields();

            setRequiresManualPayerName(
                false
            );

            setPreviousBalance(
                null
            );

        }

    }, [
        open,
        mode,
        editingPayment,
        form,
    ]);


    /* =====================================================
       MANUAL PAYER SWITCH
    ===================================================== */

    const handleManualPayerChange =
        checked => {

            setRequiresManualPayerName(
                checked
            );


            form.setFieldsValue({

                requires_manual_payer_name:
                    checked,

                student:
                    checked
                        ? undefined
                        : form.getFieldValue(
                            "student"
                        ),

                payer_name:
                    checked
                        ? form.getFieldValue(
                            "payer_name"
                        )
                        : undefined,

            });


            if (checked) {

                setPreviousBalance(
                    null
                );

            }

        };


    /* =====================================================
       STUDENT SEARCH
    ===================================================== */

    const handleStudentSearch =
        value => {

            const search =
                value?.trim() || "";


            setStudentSearch(
                search
            );


            /*
             * Cancel previous search request
             * timer.
             */

            if (
                studentSearchTimer.current
            ) {

                clearTimeout(
                    studentSearchTimer.current
                );

            }


            /*
             * Debounce by 350ms.
             *
             * This means typing:
             *
             * F
             * Fl
             * Flo
             * Flom
             * Flomo
             *
             * doesn't immediately make 5 requests.
             */

            studentSearchTimer.current =
                setTimeout(
                    () => {

                        setStudentPage(
                            1
                        );

                        setStudentHasMore(
                            true
                        );


                        loadStudents({

                            search,

                            page: 1,

                            append: false,

                        });

                    },
                    350
                );

        };


    /* =====================================================
       STUDENT DROPDOWN SCROLL
    ===================================================== */

    const handleStudentPopupScroll =
        event => {

            const target =
                event.target;


            const reachedBottom =
                target.scrollTop +
                    target.offsetHeight >=
                target.scrollHeight -
                    30;


            if (
                !reachedBottom
            ) {
                return;
            }


            if (
                studentLoading
            ) {
                return;
            }


            if (
                !studentHasMore
            ) {
                return;
            }


            loadStudents({

                search:
                    studentSearch,

                page:
                    studentPage + 1,

                append:
                    true,

            });

        };


    /* =====================================================
       PREVIOUS BALANCE
    ===================================================== */

    const fetchPreviousBalance =
        async (
            studentId,
            feeTypeId
        ) => {

            if (
                !studentId ||
                !feeTypeId
            ) {

                setPreviousBalance(
                    null
                );

                return;

            }


            try {

                const response =
                    await api.get(
                        "/other-fee-payments/autofill/",
                        {
                            params: {

                                student:
                                    studentId,

                                fee_type:
                                    feeTypeId,

                            },
                        }
                    );


                const data =
                    response.data;


                form.setFieldsValue({

                    total_amount:
                        Number(
                            data.total_amount
                        ),

                });


                if (
                    data.has_previous_balance
                ) {

                    setPreviousBalance(
                        Number(
                            data.balance
                        )
                    );

                } else {

                    setPreviousBalance(
                        null
                    );

                }

            } catch (error) {

                console.error(
                    "Previous balance error:",
                    error
                );


                setPreviousBalance(
                    null
                );

            }

        };


    /* =====================================================
       SUBMIT
    ===================================================== */

    const handleSubmit =
        async values => {

            try {

                setLoading(
                    true
                );


                /* =========================================
                   CREATE FEE TYPE
                ========================================= */

                if (
                    mode === "fee_type"
                ) {

                    const response =
                        await api.post(
                            "/other-fee-types/",
                            {
                                fee_type_name:
                                    values.fee_type_name,

                                total_amount:
                                    values.total_amount,
                            }
                        );


                    message.success(
                        "Fee type created successfully."
                    );


                    onFeeTypeCreated(
                        response.data
                    );


                    form.resetFields();

                    onClose();

                    return;
                }


                /* =========================================
                   PAYMENT PAYLOAD
                ========================================= */

                const payload = {

                    fee_type:
                        values.fee_type,

                    requires_manual_payer_name:
                        requiresManualPayerName,

                    student:
                        requiresManualPayerName
                            ? null
                            : values.student,

                    payer_name:
                        requiresManualPayerName
                            ? values.payer_name
                            : null,

                    total_amount:
                        values.total_amount,

                    amount_paid:
                        values.amount_paid,

                    month:
                        values.month,

                };


                /* =========================================
                   UPDATE PAYMENT
                ========================================= */

                if (
                    editingPayment
                ) {

                    const response =
                        await api.patch(
                            `/other-fee-payments/${editingPayment.id}/`,
                            payload
                        );


                    message.success(
                        "Payment updated successfully."
                    );


                    onPaymentUpdated(
                        response.data
                    );

                }

                /* =========================================
                   CREATE PAYMENT
                ========================================= */

                else {

                    const response =
                        await api.post(
                            "/other-fee-payments/",
                            payload
                        );


                    message.success(
                        "Payment created successfully."
                    );


                    onPaymentCreated(
                        response.data
                    );

                }


                form.resetFields();

                setStudentOptions([]);

                setStudentSearch("");

                setPreviousBalance(
                    null
                );

                onClose();

            } catch (error) {

                console.error(
                    "Fee form error:",
                    error
                );


                const errorData =
                    error.response?.data;


                if (
                    errorData &&
                    typeof errorData ===
                        "object"
                ) {

                    const formattedErrors =
                        Object.entries(
                            errorData
                        ).map(
                            (
                                [
                                    field,
                                    errors,
                                ]
                            ) => {

                                const errorMessage =
                                    Array.isArray(
                                        errors
                                    )
                                        ? errors.join(
                                            ", "
                                        )
                                        : String(
                                            errors
                                        );


                                return (
                                    `${field}: ${errorMessage}`
                                );

                            }
                        );


                    message.error(
                        formattedErrors.join(
                            " | "
                        )
                    );

                } else {

                    message.error(
                        "Unable to process request."
                    );

                }

            } finally {

                setLoading(
                    false
                );

            }

        };


    /* =====================================================
       STUDENT OPTIONS
    ===================================================== */

    const renderedStudentOptions =
        useMemo(() => {

            return studentOptions.map(
                student => {

                    const fullName =
                        student.full_name ||
                        [
                            student.first_name,
                            student.middle_name,
                            student.last_name,
                        ]
                            .filter(Boolean)
                            .join(" ")
                            .trim();


                    const admissionNumber =
                        student.admission_number ||
                        student.admission_no ||
                        student.admission_id;

                        

                    const className =
                        student.grade_class_name ||
                        student.grade_class?.name ||
                        student.class_name;


                    return {

                        value:
                            student.id,

                        label: (

                            <div
                                style={{
                                    display:
                                        "flex",

                                    flexDirection:
                                        "column",

                                    lineHeight:
                                        1.3,

                                    padding:
                                        "3px 0",
                                }}
                            >

                                <span
                                    style={{
                                        fontWeight:
                                            600,
                                    }}
                                >

                                    {
                                        fullName ||
                                        `Student #${student.id}`
                                    }

                                </span>


                                {admissionNumber && (

                                    <span
                                        style={{
                                            fontSize:
                                                12,

                                            color:
                                                "#888",

                                            marginTop:
                                                2,
                                        }}
                                    >

                                        Admission No:
                                        {" "}
                                        {
                                            admissionNumber
                                        }

                                    </span>

                                )}


                                {className && (

                                    <span
                                        style={{
                                            fontSize:
                                                12,

                                            color:
                                                "#999",

                                            marginTop:
                                                1,
                                        }}
                                    >

                                        Class:
                                        {" "}
                                        {
                                            className
                                        }

                                    </span>

                                )}

                            </div>

                        ),

                    };

                }
            );

        }, [
            studentOptions,
        ]);


    /* =====================================================
       MODAL
    ===================================================== */

    return (

        <Modal

            open={
                open
            }

            onCancel={
                onClose
            }

            footer={
                null
            }

            centered

            destroyOnClose

            width={
                mode === "fee_type"
                    ? 500
                    : 700
            }

            title={

                <div
                    style={{
                        display:
                            "flex",

                        flexDirection:
                            "column",

                        gap:
                            4,
                    }}
                >

                    <div
                        style={{
                            display:
                                "flex",

                            alignItems:
                                "center",

                            gap:
                                10,

                            fontWeight:
                                700,

                            fontSize:
                                18,
                        }}
                    >

                        <DollarOutlined />

                        {
                            editingPayment
                                ? "Edit Fee Payment"
                                : "Process Other Fee Payment"
                        }

                    </div>


                    {previousBalance !== null && (

                        <div
                            style={{
                                color:
                                    "#fa541c",

                                fontWeight:
                                    700,

                                fontSize:
                                    14,
                            }}
                        >

                            Outstanding Balance:
                            {" "}

                            {
                                previousBalance.toFixed(
                                    2
                                )
                            }

                        </div>

                    )}

                </div>

            }

        >

            <Form

                form={
                    form
                }

                layout="vertical"

                onFinish={
                    handleSubmit
                }

            >

                {/* =================================================
                    FEE TYPE CREATION MODE
                ================================================= */}

                {mode === "fee_type" ? (

                    <>

                        <Form.Item

                            label="Fee Type Name"

                            name="fee_type_name"

                            rules={[
                                {
                                    required:
                                        true,

                                    message:
                                        "Please enter the fee type name.",
                                },

                                {
                                    min:
                                        2,

                                    message:
                                        "Fee type name must contain at least 2 characters.",
                                },
                            ]}

                        >

                            <Input

                                placeholder="e.g. Graduation Fee"

                                size="large"

                            />

                        </Form.Item>


                        <Form.Item

                            label="Total Amount"

                            name="total_amount"

                            rules={[
                                {
                                    required:
                                        true,

                                    message:
                                        "Please enter the fee amount.",
                                },
                            ]}

                        >

                            <InputNumber

                                min={
                                    0
                                }

                                precision={
                                    2
                                }

                                style={{
                                    width:
                                        "100%",
                                }}

                                size="large"

                                placeholder="0.00"

                            />

                        </Form.Item>


                        <Divider />


                        <Space
                            style={{
                                width:
                                    "100%",

                                justifyContent:
                                    "flex-end",
                            }}
                        >

                            <Button

                                icon={
                                    <CloseOutlined />
                                }

                                onClick={
                                    onClose
                                }

                            >

                                Cancel

                            </Button>


                            <Button

                                type="primary"

                                htmlType="submit"

                                loading={
                                    loading
                                }

                                icon={
                                    <PlusOutlined />
                                }

                            >

                                Add Fee Type

                            </Button>

                        </Space>

                    </>

                ) : (

                    <>

                        {/* =================================================
                            FEE TYPE
                        ================================================= */}

                        <Form.Item

                            label="Fee Type"

                            name="fee_type"

                            rules={[
                                {
                                    required:
                                        true,

                                    message:
                                        "Please select a fee type.",
                                },
                            ]}

                        >

                            <Select

                                placeholder="Select fee type"

                                size="large"

                                showSearch

                                optionFilterProp="children"

                                onChange={
                                    async value => {

                                        form.setFieldsValue(
                                            {
                                                fee_type:
                                                    value,
                                            }
                                        );


                                        const studentId =
                                            form.getFieldValue(
                                                "student"
                                            );


                                        if (
                                            studentId
                                        ) {

                                            await fetchPreviousBalance(
                                                studentId,
                                                value
                                            );

                                        } else {

                                            const selectedFee =
                                                feeTypeMap[
                                                    value
                                                ];


                                            form.setFieldsValue(
                                                {
                                                    total_amount:
                                                        selectedFee
                                                            ? Number(
                                                                selectedFee.total_amount
                                                            )
                                                            : 0,
                                                }
                                            );


                                            setPreviousBalance(
                                                null
                                            );

                                        }

                                    }
                                }

                            >

                                {feeTypes.map(
                                    feeType => (

                                        <Option

                                            key={
                                                feeType.id
                                            }

                                            value={
                                                feeType.id
                                            }

                                        >

                                            {
                                                feeType.fee_type_name
                                            }

                                        </Option>

                                    )
                                )}

                            </Select>

                        </Form.Item>


                        {/* =================================================
                            MANUAL PAYER
                        ================================================= */}

                        <Form.Item

                            label="Manual Payer (Ex. Entrance Fees ETC)"

                            name="requires_manual_payer_name"

                            valuePropName="checked"

                        >

                            <Switch

                                checked={
                                    requiresManualPayerName
                                }

                                onChange={
                                    handleManualPayerChange
                                }

                            />

                        </Form.Item>


                        {/* =================================================
                            STUDENT AUTOCOMPLETE
                        ================================================= */}

                        {!requiresManualPayerName ? (

                            <Form.Item

                                label="Student"

                                name="student"

                                rules={[
                                    {
                                        required:
                                            true,

                                        message:
                                            "Please select a student.",
                                    },
                                ]}

                            >

                                <Select

                                    placeholder={
                                        studentLoading
                                            ? "Loading students..."
                                            : "Search student by name or admission number..."
                                    }

                                    size="large"

                                    showSearch

                                    allowClear

                                    /*
                                     * VERY IMPORTANT.
                                     *
                                     * Ant Design must NOT perform
                                     * local filtering.
                                     *
                                     * Django performs the search.
                                     */

                                    filterOption={
                                        false
                                    }

                                    loading={
                                        studentLoading
                                    }

                                    notFoundContent={

                                        studentLoading ? (

                                            <div
                                                style={{
                                                    padding:
                                                        14,

                                                    textAlign:
                                                        "center",
                                                }}
                                            >

                                                <Spin
                                                    size="small"
                                                />

                                                <div
                                                    style={{
                                                        marginTop:
                                                            7,

                                                        color:
                                                            "#888",

                                                        fontSize:
                                                            13,
                                                    }}
                                                >

                                                    Searching students...

                                                </div>

                                            </div>

                                        ) : studentSearch ? (

                                            <div
                                                style={{
                                                    padding:
                                                        14,

                                                    textAlign:
                                                        "center",

                                                    color:
                                                        "#888",
                                                }}
                                            >

                                                No student found for
                                                {" "}
                                                <strong>
                                                    "{studentSearch}"
                                                </strong>

                                            </div>

                                        ) : (

                                            "No students found"

                                        )

                                    }

                                    onSearch={
                                        handleStudentSearch
                                    }

                                    onPopupScroll={
                                        handleStudentPopupScroll
                                    }

                                    onChange={
                                        async studentId => {

                                            if (
                                                !studentId
                                            ) {

                                                setPreviousBalance(
                                                    null
                                                );

                                                return;

                                            }


                                            const feeTypeId =
                                                form.getFieldValue(
                                                    "fee_type"
                                                );


                                            if (
                                                feeTypeId
                                            ) {

                                                await fetchPreviousBalance(
                                                    studentId,
                                                    feeTypeId
                                                );

                                            }

                                        }
                                    }

                                    options={
                                        renderedStudentOptions
                                    }

                                    popupMatchSelectWidth={
                                        true
                                    }

                                />

                            </Form.Item>

                        ) : (

                            /* =================================================
                               MANUAL PAYER
                            ================================================= */

                            <Form.Item

                                label="Payer Name"

                                name="payer_name"

                                rules={[
                                    {
                                        required:
                                            true,

                                        message:
                                            "Please enter the payer name.",
                                    },
                                ]}

                            >

                                <Input

                                    placeholder="Enter payer name"

                                    size="large"

                                />

                            </Form.Item>

                        )}


                        {/* =================================================
                            AMOUNTS
                        ================================================= */}

                        <div
                            style={{
                                display:
                                    "grid",

                                gridTemplateColumns:
                                    "1fr 1fr",

                                gap:
                                    16,
                            }}
                        >

                            <Form.Item

                                label="Total Amount"

                                name="total_amount"

                                rules={[
                                    {
                                        required:
                                            true,

                                        message:
                                            "Please enter the total amount.",
                                    },
                                ]}

                            >

                                <InputNumber

                                    min={
                                        0
                                    }

                                    precision={
                                        2
                                    }

                                    disabled

                                    controls={
                                        false
                                    }

                                    style={{
                                        width:
                                            "100%",
                                    }}

                                    size="large"

                                    placeholder="0.00"

                                />

                            </Form.Item>


                            <Form.Item

                                label="Amount Paid"

                                name="amount_paid"

                                rules={[
                                    {
                                        required:
                                            true,

                                        message:
                                            "Please enter the amount paid.",
                                    },
                                ]}

                            >

                                <InputNumber

                                    min={
                                        0
                                    }

                                    precision={
                                        2
                                    }

                                    style={{
                                        width:
                                            "100%",
                                    }}

                                    size="large"

                                    placeholder="0.00"

                                />

                            </Form.Item>

                        </div>


                        {/* =================================================
                            PAYMENT MONTH
                        ================================================= */}

                        <Form.Item

                            label="Payment Month"

                            name="month"

                        >

                            <Select

                                placeholder="Select month"

                                size="large"

                                allowClear

                            >

                                {MONTHS.map(
                                    month => (

                                        <Option

                                            key={
                                                month.value
                                            }

                                            value={
                                                month.value
                                            }

                                        >

                                            {
                                                month.label
                                            }

                                        </Option>

                                    )
                                )}

                            </Select>

                        </Form.Item>


                        <Divider />


                        {/* =================================================
                            ACTION BUTTONS
                        ================================================= */}

                        <Space

                            style={{
                                width:
                                    "100%",

                                justifyContent:
                                    "flex-end",
                            }}

                        >

                            <Button

                                onClick={
                                    onClose
                                }

                                icon={
                                    <CloseOutlined />
                                }

                            >

                                Cancel

                            </Button>


                            <Button

                                type="primary"

                                htmlType="submit"

                                loading={
                                    loading
                                }

                                icon={
                                    <DollarOutlined />
                                }

                            >

                                {
                                    editingPayment
                                        ? "Update Payment"
                                        : "Process Payment"
                                }

                            </Button>

                        </Space>

                    </>

                )}

            </Form>

        </Modal>

    );

};


export default FeesForm;